import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import LineChart from "../../components/analytics/LineChart"
import BarChart from "../../components/analytics/BarChart"
import PostGrid from "../../components/analytics/PostGrid"
import SummaryTable from "../../components/analytics/SummaryTable"
import DateRangePicker, { type DateRange } from "../../components/analytics/DateRangePicker"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import type { HashtagDetail } from "../../types/analytics"
import "../../styles/analytics.scss"

const TREND_TABS = [
  { key: "reach",           label: "リーチ" },
  { key: "engagement_rate", label: "ENG率" },
  { key: "likes",           label: "いいね" },
  { key: "comments",        label: "コメント" },
  { key: "saves",           label: "保存" },
]

const COMPARISON_LABELS: Record<string, string> = {
  reach:           "リーチ",
  likes:           "いいね",
  comments:        "コメント",
  saves:           "保存",
  shares:          "シェア",
  engagement_rate: "ENG率",
}

const toDateStr = (d: Date) => d.toISOString().split("T")[0]

const HashtagDetailPage = () => {
  const params = useParams()
  const tag = params["*"] ?? ""
  const { selectedAccountId, selectedAccount, setSingleAccountOnly } = useSelectedAccount()
  const navigate = useNavigate()
  const trendChartRef      = useRef<HTMLDivElement>(null)
  const comparisonChartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [detail, setDetail]     = useState<HashtagDetail | null>(null)
  const [loading, setLoading]   = useState(true)
  const [trendTab, setTrendTab] = useState("reach")
  const [range, setRange]       = useState<DateRange>(() => {
    const end = new Date(); const start = new Date()
    start.setDate(start.getDate() - 29)
    return { startDate: toDateStr(start), endDate: toDateStr(end) }
  })

  const fetchData = useCallback(async () => {
    if (!selectedAccountId || !tag) { setLoading(false); return }
    setLoading(true)
    try {
      const queryParams = new URLSearchParams({
        instagram_account_id: String(selectedAccountId),
        start_date: range.startDate,
        end_date:   range.endDate,
      })
      const res = await api.get<HashtagDetail>(`/analytics/hashtags/${tag}?${queryParams}`)
      setDetail(res)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId, tag, range])

  useEffect(() => { fetchData() }, [fetchData])

  const trendChartData = detail?.time_series.length ? {
    labels:   detail.time_series.map(t => t.week),
    datasets: [{ data: detail.time_series.map(t => (t[trendTab as keyof typeof t] as number | null) ?? 0) }],
  } : null

  const comparisonData = detail?.comparison ? {
    labels: detail.comparison.metrics.map(m => COMPARISON_LABELS[m] ?? m),
    datasets: [
      {
        label:           `#${detail.tag}`,
        data:            detail.comparison.metrics.map(m => detail.comparison.tag[m] ?? 0),
        backgroundColor: colors.primary,
      },
      {
        label:           "全体平均",
        data:            detail.comparison.metrics.map(m => detail.comparison.overall[m] ?? 0),
        backgroundColor: colors.gray400,
      },
    ],
  } : null

  const s = detail?.summary

  return (
    <AppLayout pageTitle="ハッシュタグ詳細" onBack={() => navigate("/hashtags")}>
      <div className="analytics">
        <div className="analytics__header" style={{ alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>#{tag}</h2>
          </div>
          <DateRangePicker value={range} onChange={setRange} maxDays={selectedAccount?.plan === "free" ? selectedAccount.features.free_window_days : undefined} />
        </div>

        {loading ? (
          <p style={{ color: colors.gray400 }}>読み込み中…</p>
        ) : !detail ? (
          <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
        ) : (
          <>
            <div className="analytics__stats-grid">
              <StatCard label="投稿数"    stat={{ value: s?.posts_count ?? null,         change_rate: null }} unit="件" />
              <StatCard label="平均リーチ" stat={{ value: s?.avg_reach ?? null,           change_rate: null }} />
              <StatCard label="平均ENG率" stat={{ value: s?.avg_engagement_rate ?? null, change_rate: null }} unit="%" />
              <StatCard label="平均いいね" stat={{ value: s?.avg_likes ?? null,           change_rate: null }} />
            </div>

            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">週次推移</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="filter-tabs" style={{ margin: 0 }}>
                      {TREND_TABS.map(t => (
                        <button
                          key={t.key}
                          className={`filter-tabs__item${trendTab === t.key ? " filter-tabs__item--active" : ""}`}
                          onClick={() => setTrendTab(t.key)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {trendChartData && (
                      <div className="chart-card__actions">
                        <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(trendChartData), `hashtag_trend_${trendTab}_${todayStr()}.csv`)}>↓ CSV</button>
                        <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(trendChartRef.current, `hashtag_trend_${trendTab}_${todayStr()}.png`)}>↓ PNG</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="chart-card__canvas-wrap" ref={trendChartRef}>
                  {trendChartData
                    ? <LineChart data={trendChartData} />
                    : <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                  }
                </div>
              </div>
            </div>

            <div className="analytics__section">
              {comparisonData && (
                <div className="chart-card">
                  <div className="chart-card__header">
                    <span className="chart-card__title">全体平均との比較</span>
                    <div className="chart-card__actions">
                      <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(comparisonData), `hashtag_comparison_chart_${todayStr()}.csv`)}>↓ CSV</button>
                      <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(comparisonChartRef.current, `hashtag_comparison_chart_${todayStr()}.png`)}>↓ PNG</button>
                    </div>
                  </div>
                  <div className="chart-card__canvas-wrap" ref={comparisonChartRef}>
                    <BarChart data={comparisonData} horizontal />
                  </div>
                </div>
              )}
              <SummaryTable
                rows={[
                  { label: `#${detail.tag}`, values: detail.comparison.tag },
                  { label: "全体平均",        values: detail.comparison.overall },
                ]}
                columns={detail.comparison.metrics.map(m => ({ key: m, label: COMPARISON_LABELS[m] ?? m }))}
                title="全体平均との比較"
                filePrefix="hashtag_comparison"
              />
            </div>

            <div className="analytics__charts-grid">
              <div className="top-posts">
                <div className="top-posts__header">
                  <span className="top-posts__title">ベスト投稿 TOP3（ENG率）</span>
                </div>
                {detail.best_posts.length > 0
                  ? <PostGrid posts={detail.best_posts} aspect="square" onPostClick={(id, accountId) => navigate(`/feed-posts/${id}`, { state: { accountId: accountId ?? selectedAccountId } })} />
                  : <div className="top-posts__empty">データ収集中です</div>
                }
              </div>
              <div className="top-posts">
                <div className="top-posts__header">
                  <span className="top-posts__title">改善が必要な投稿 TOP3（ENG率）</span>
                </div>
                {detail.worst_posts.length > 0
                  ? <PostGrid posts={detail.worst_posts} aspect="square" onPostClick={(id, accountId) => navigate(`/feed-posts/${id}`, { state: { accountId: accountId ?? selectedAccountId } })} />
                  : <div className="top-posts__empty">データ収集中です</div>
                }
              </div>
            </div>

            <div className="analytics__section">
              <div className="posts-table">
                <div className="posts-table__header">
                  <span className="posts-table__title">投稿一覧</span>
                  <button
                    className="summary-table__csv-btn"
                    onClick={() => downloadCsv(
                      [
                        ["キャプション", "投稿日時", "いいね", "コメント", "保存", "リーチ", "ENG率(%)"],
                        ...detail.posts.map(p => [
                          p.caption ?? "", p.posted_at ?? "",
                          p.likes, p.comments, p.saves, p.reach, p.engagement_rate,
                        ]),
                      ],
                      `hashtag_posts_${todayStr()}.csv`
                    )}
                  >
                    ↓ CSV
                  </button>
                </div>
                <div className="posts-table__wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>投稿</th>
                        <th>いいね</th>
                        <th>コメント</th>
                        <th>保存</th>
                        <th>リーチ</th>
                        <th>ENG率</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.posts.map(post => (
                        <tr
                          key={post.id}
                          className="posts-table__row--clickable"
                          onClick={() => navigate(`/feed-posts/${post.id}`, { state: { accountId: post.instagram_account_id ?? selectedAccountId } })}
                        >
                          <td>
                            <div className="posts-table__thumb-cell">
                              {post.thumbnail_url
                                ? <img src={post.thumbnail_url} alt="" className="posts-table__thumbnail" />
                                : <div className="posts-table__no-thumb">📷</div>
                              }
                              <span className="posts-table__caption">{post.caption ?? "—"}</span>
                            </div>
                          </td>
                          <td>{post.likes?.toLocaleString() ?? "—"}</td>
                          <td>{post.comments?.toLocaleString() ?? "—"}</td>
                          <td>{post.saves?.toLocaleString() ?? "—"}</td>
                          <td>{post.reach?.toLocaleString() ?? "—"}</td>
                          <td>{post.engagement_rate != null ? `${post.engagement_rate}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default HashtagDetailPage
